// SPDX-License-Identifier: AGPL-3.0-or-later

import {isHolidayCategoryEnabled} from '@app/features/holiday/state/HolidayPreferences';
import {holidayIntensity} from '@app/features/holiday/utils/holidayIntensity';
import {isHolidaySeasonEnabled} from '@app/features/holiday/utils/holidaySeason';

/** DEV-only. `true` spawns scares out of season and ignores chance. Production builds never force. */
export const DEBUG_FORCE_SKELETON_SCARE: true | null = null;

/** Triggers that can ask for a scare. Each maps to its own chance below. */
export type HolidayScareReason = 'ambient' | 'spoiler-reveal' | 'channel-switch' | 'send';

/**
 * Scares are a late-season effect. The master plan's window for the skeleton is Oct 16–30, and the
 * shared curve reaches 0.291 on Oct 16 (0.274 on the 15th), so this threshold is that date
 * expressed against the ramp rather than as another hardcoded calendar check.
 */
export const HALLOWEEN_SCARE_MIN_INTENSITY = 0.29;
/**
 * Six times the previous 30min/6min spacing. Wide enough that the cooldown, not the per-trigger
 * chance, is now what paces scares: at peak the ambient roll alone would land one about every
 * 50min, so the 36min floor is what a run of lucky rolls actually hits.
 */
export const SCARE_COOLDOWN_MAX_MS = 180 * 60 * 1000;
export const SCARE_COOLDOWN_MIN_MS = 36 * 60 * 1000;

/** Half the previous chances across every trigger, on top of the widened cooldown above. */
export const SCARE_AMBIENT_CHANCE_MIN = 0.02;
export const SCARE_AMBIENT_CHANCE_MAX = 0.08;
export const SCARE_SPOILER_CHANCE_MIN = 0.03;
export const SCARE_SPOILER_CHANCE_MAX = 0.11;
export const SCARE_CHANNEL_SWITCH_CHANCE_MIN = 0.005;
export const SCARE_CHANNEL_SWITCH_CHANCE_MAX = 0.025;
export const SCARE_SEND_CHANCE_MIN = 0.0025;
export const SCARE_SEND_CHANCE_MAX = 0.015;

const CHANCE_RANGES: Record<HolidayScareReason, {min: number; max: number}> = {
	ambient: {min: SCARE_AMBIENT_CHANCE_MIN, max: SCARE_AMBIENT_CHANCE_MAX},
	'spoiler-reveal': {min: SCARE_SPOILER_CHANCE_MIN, max: SCARE_SPOILER_CHANCE_MAX},
	'channel-switch': {min: SCARE_CHANNEL_SWITCH_CHANCE_MIN, max: SCARE_CHANNEL_SWITCH_CHANCE_MAX},
	send: {min: SCARE_SEND_CHANCE_MIN, max: SCARE_SEND_CHANCE_MAX},
};

function clamp(value: number, min: number, max: number): number {
	return Math.min(max, Math.max(min, value));
}

function lerp(from: number, to: number, t: number): number {
	return from + (to - from) * t;
}

export function isHalloweenScareForced(): boolean {
	return import.meta.env.DEV && DEBUG_FORCE_SKELETON_SCARE === true;
}

/** Season and preference gate, so callers do not tick timers out of season. */
export function isHalloweenScaresEnabled(): boolean {
	if (isHalloweenScareForced()) return true;
	if (!isHolidaySeasonEnabled('halloween')) return false;
	return isHolidayCategoryEnabled('scares', 'halloween');
}

export function halloweenScareIntensity(now: Date): number {
	return holidayIntensity('halloween', now);
}

/** 0 below the late-season threshold, then 0→1 across the remaining ramp. */
export function halloweenScareProgress(intensity: number): number {
	if (intensity < HALLOWEEN_SCARE_MIN_INTENSITY) return 0;
	return clamp((intensity - HALLOWEEN_SCARE_MIN_INTENSITY) / (1 - HALLOWEEN_SCARE_MIN_INTENSITY), 0, 1);
}

export function halloweenScareChance(reason: HolidayScareReason, intensity: number): number {
	const range = CHANCE_RANGES[reason];
	return lerp(range.min, range.max, halloweenScareProgress(intensity));
}

export function halloweenScareCooldownMs(intensity: number): number {
	return Math.round(lerp(SCARE_COOLDOWN_MAX_MS, SCARE_COOLDOWN_MIN_MS, halloweenScareProgress(intensity)));
}
